// 設備所在的路徑（「EddyStudio › lab」；設備清單名稱下方的小字、設備詳細概覽，SPEC DEV-01 / DEV-05）：
// 照頁首範圍樹（我的設備的葉節點），不含「我自己的 / 組織授權給我」這層來源（「別人分享給我」保留）；查不到用家戶名稱。
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useScope } from '../../lib/scope';
import { leafOf, nodePath, type ScopeNode } from '../../lib/myDevices';
import { scopeNodeName } from '../../lib/scopeName';
import { CHILD_SEP, type VmsDevice } from '../../lib/devices';

/**
 * 範圍樹路徑 → 顯示字串（設備清單「依位置」的群組名、名稱下方的路徑、影像牆範圍外角標同一個函式）：從組織名開始寫，
 * 不含「我自己的 / 組織授權給我」這層來源；但「別人分享給我」與外部分享保留來源字樣（不然只剩一個人名 / 「外部分享」，SPEC #vms-web-290 第六輪）。
 */
export function pathLabel(t: (k: string, o?: Record<string, unknown>) => string, path: ScopeNode[]): string {
  const keepSource = path.some((n) => n.kind === 'sharer' || n.kind === 'external');
  return path
    .filter((n) => keepSource || n.kind !== 'source')
    .map((n) => scopeNodeName(t, n))
    .join(' › ');
}

export function useDevicePath(): (d: Pick<VmsDevice, 'key' | 'homeName'>) => string {
  const { t } = useTranslation();
  // 用全部的設備（不只目前範圍）：影像牆上不在目前範圍的格子也要寫得出「EddyStudio › lab」（第五輪 B5-07）
  const { tree: groups, visibleDevices, devices } = useScope();
  return useMemo(() => {
    const byKey = new Map([...devices, ...visibleDevices].map((m) => [`${m.home_id}/${m.device_ref}`, m]));
    return (d) => {
      // NVR / Kinio Station 的通道不在「我的設備」清單裡：用它的錄影主機找路徑（第六輪 B6-05：NVR631 的通道角標只寫「lab」）
      const i = d.key.lastIndexOf(CHILD_SEP);
      const me = byKey.get(d.key) ?? (i > 0 ? byKey.get(d.key.slice(0, i)) : undefined);
      if (!me) return d.homeName;
      return pathLabel(t, nodePath(groups, leafOf(me, groups).key)) || d.homeName;
    };
  }, [groups, visibleDevices, devices, t]);
}

/** 家戶的完整路徑（「LivingStyle › lstw › rd」；兩個同名的 rd 分得出來，第五輪 B5-14）；樹上沒有用 fallback。 */
export function useHomePath(): (homeId: string, fallback?: string) => string {
  const { t } = useTranslation();
  const { tree: groups } = useScope();
  return useMemo(
    () => (homeId, fallback) =>
      nodePath(groups, 'home:' + homeId)
        .filter((n) => n.kind !== 'source')
        .map((n) => scopeNodeName(t, n))
        .join(' › ') ||
      fallback ||
      homeId,
    [groups, t],
  );
}
